import { memo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { format, parseISO, isToday } from 'date-fns'
import { Briefcase } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import { formatNaira } from '@/lib/format'
import { ChannelBadge } from '@/features/transactions/components/ChannelBadge'
import { PriorityDot } from './PriorityDot'
import { ScoreCell } from './ScoreCell'
import { SlaCell } from './SlaCell'
import { AssigneeAvatar } from './AssigneeAvatar'
import { StatusIcon } from './StatusIcon'
import { hasSummary } from '../../types'
import type { AlertSummary } from '../../types'

interface Props {
  alert: AlertSummary
  selected: boolean
  focused: boolean
  recentlyChanged: boolean
  /** Called with this row's alert ID on any pointer interaction inside it. */
  onFocusRow?: (id: string) => void
  onToggleSelect: (id: string, range: boolean) => void
}

/**
 * AF-126 — the workhorse row. Memoised because the queue rerenders on every
 * filter / sort / WS event; without memo, scrolling 1000 rows is jank.
 *
 * The grid template here is the single source of truth for column widths;
 * the table header (in AlertsTable.tsx) mirrors it exactly.
 */
function AlertRowImpl({
  alert,
  selected,
  focused,
  recentlyChanged,
  onFocusRow,
  onToggleSelect,
}: Props) {
  const created = parseISO(alert.created_at)
  const navigate = useNavigate()
  return (
    // The row is already fully keyboard-operable without native DOM
    // focus: useQueueKeyboard runs a custom j/k/Enter model against a
    // virtual `focusedIndex` (Enter already opens the focused row).
    // Making 5000 virtualized rows real tab-stops would be a worse a11y
    // outcome, not a better one — the onDoubleClick handler below only
    // adds a mouse-only convenience on top of the existing keyboard path.
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      data-row-id={alert.id}
      role="row"
      aria-selected={selected}
      // KUP-API-003: any pointer press inside the row (including non-link
      // areas) or DOM focus landing on a control inside it (Tab into a
      // link/checkbox) makes THIS row the keyboard-focused one, so a
      // following Enter opens the row the operator just interacted with.
      onPointerDown={() => onFocusRow?.(alert.id)}
      onFocus={() => onFocusRow?.(alert.id)}
      onDoubleClick={(e) => {
        // Double-click-to-open, without disturbing single-click row
        // selection: the row has no single-click handler of its own
        // (only the checkbox toggles selection, and the Link cells
        // already single-click-navigate), so this only ever ADDS a
        // second, faster way to open the alert. Ignore the checkbox
        // itself — a double-click there should just toggle selection
        // twice (net no-op), not also navigate away underneath it. The
        // checkbox sits inside a larger centered Cell, so guard the whole
        // cell (via data-select-cell) rather than just the 16x16 input —
        // otherwise a double-click on the cell's padding still falls
        // through to navigate() even though it visually reads as "the
        // checkbox column".
        if (
          e.target instanceof Element &&
          e.target.closest('[data-select-cell]')
        ) {
          return
        }
        void navigate(`/alerts/${alert.id}`)
      }}
      className={cn(
        'group grid h-10 items-center gap-3 border-b border-border/60 px-3 text-sm transition-colors',
        // Same template the header uses.
        // Column widths: checkbox | priority | alert-number | scenario+case
        // | score | customer | amount | channel | status | sla | assigned | created.
        // Priority (col 2) and Assigned (col 11) are widened past the
        // size of their body glyphs so the header labels fit without
        // overflowing into the next column. Scenario uses 1fr (not 2fr)
        // so on wide screens it doesn't outstretch Customer and leave a
        // yawning gap before Score.
        'grid-cols-[40px_48px_96px_minmax(20rem,1.4fr)_56px_minmax(10rem,1fr)_128px_56px_24px_112px_80px_80px]',
        'hover:bg-muted/40',
        focused && 'bg-primary/[0.06] shadow-[inset_2px_0_0_var(--color-primary)]',
        selected && !focused && 'bg-primary/5 shadow-[inset_3px_0_0_var(--color-primary)]',
        alert.sla_breached &&
          !selected &&
          !focused &&
          'shadow-[inset_3px_0_0_var(--color-status-failed)]',
        recentlyChanged && 'cell-pulse',
      )}
    >
      {/* Every direct child of role="row" inside a grid must be a
          gridcell / rowheader / columnheader, per WAI-ARIA. The
          previous bare span/Link/component children tripped the
          aria-required-children axe rule. The Cell wrapper here
          adds role="gridcell" without changing layout — each cell
          still sits in its own grid column. */}
      <Cell data-select-cell className="flex items-center justify-center">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => {
            const native = e.nativeEvent
            const isShift = native instanceof MouseEvent ? native.shiftKey : false
            onToggleSelect(alert.id, isShift)
          }}
          aria-label={`Select alert ${alert.alert_number}`}
          className="h-4 w-4 cursor-pointer accent-primary"
        />
      </Cell>

      <Cell>
        <PriorityDot priority={alert.priority} />
      </Cell>

      <Cell>
        <Link
          to={`/alerts/${alert.id}`}
          className="truncate font-mono text-xs tabular-nums text-muted-fg hover:text-fg"
        >
          {alert.alert_number}
        </Link>
      </Cell>

      <Cell className="min-w-0">
        <Link
          to={`/alerts/${alert.id}`}
          className="flex min-w-0 items-center gap-1.5 truncate text-fg hover:underline"
          // The queue row is a fixed-height virtualized grid (see
          // ROW_HEIGHT in AlertsTable.tsx) with a documented
          // 12-column budget shared with AlertsTableHeader — there's
          // no room for a dedicated summary column without either
          // truncating the sentence to a few misleading words or
          // breaking the virtualizer's fixed row-height assumption.
          // Folding `summary` into this existing hover affordance
          // (already used for the truncated scenario name) surfaces
          // the full "why this fired" sentence without a layout
          // change.
          title={hasSummary(alert) ? alert.summary : alert.scenario_name}
        >
          <span className="truncate font-medium">{alert.scenario_name}</span>
          <span className="font-mono text-[0.625rem] uppercase text-muted-fg">
            {alert.scenario_code}
          </span>
          {alert.branch_code && (
            <span
              className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[0.625rem] text-primary"
              title={alert.branch_name ?? alert.branch_code}
            >
              {alert.branch_code}
            </span>
          )}
          {alert.case_id && <CaseLinkBadge caseNumber={alert.case_number} />}
        </Link>
      </Cell>

      <Cell>
        <ScoreCell score={alert.score} />
      </Cell>

      <Cell className="min-w-0">
        <Link
          to={`/customers/${alert.customer.id}`}
          className="min-w-0 truncate hover:underline"
          title={alert.customer.display_name}
        >
          {alert.customer.display_name}
        </Link>
      </Cell>

      <Cell className="text-right font-mono tabular-nums text-sm">
        {alert.amount_minor !== null ? formatNaira(alert.amount_minor) : '—'}
      </Cell>

      <Cell>
        <ChannelBadge channel={alert.channel} />
      </Cell>

      <Cell className="flex justify-center">
        <StatusIcon status={alert.status} />
      </Cell>

      <Cell>
        <SlaCell sla={alert.sla} status={alert.status} />
      </Cell>

      <Cell className="flex justify-center">
        <AssigneeAvatar assignee={alert.assigned_to} />
      </Cell>

      <Cell className="font-mono tabular-nums text-xs text-muted-fg">
        {isToday(created) ? format(created, 'HH:mm') : format(created, 'd MMM')}
      </Cell>
    </div>
  )
}

/** Cell — gridcell wrapper. Each Cell IS a grid item under the
 *  parent's grid-cols-[…] template; the per-cell className
 *  controls inner layout (flex centering, text-right, truncation).
 *  Default `min-w-0` so flex / grid children with truncate behave. */
function Cell({
  className,
  children,
  'data-select-cell': dataSelectCell,
}: {
  className?: string
  children: React.ReactNode
  'data-select-cell'?: boolean
}) {
  return (
    <div
      role="gridcell"
      data-select-cell={dataSelectCell ? '' : undefined}
      className={cn('min-w-0', className)}
    >
      {children}
    </div>
  )
}

export const AlertRow = memo(AlertRowImpl, (prev, next) => {
  // Memoize aggressively — the queue rerenders frequently from WS events.
  return (
    prev.alert === next.alert &&
    prev.selected === next.selected &&
    prev.focused === next.focused &&
    prev.onFocusRow === next.onFocusRow &&
    prev.recentlyChanged === next.recentlyChanged
  )
})

function CaseLinkBadge({ caseNumber }: { caseNumber: string | null }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex shrink-0 items-center gap-0.5 rounded-sm bg-primary/10 px-1 py-px text-[0.625rem] text-primary ring-1 ring-inset ring-primary/30"
          aria-label={caseNumber ? `In case ${caseNumber}` : 'In a case'}
        >
          <Briefcase size={9} aria-hidden />
          {caseNumber}
        </span>
      </TooltipTrigger>
      <TooltipContent>{caseNumber ? `In case ${caseNumber}` : 'In a case'}</TooltipContent>
    </Tooltip>
  )
}
